import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';

// Custom X-axis tick component (same as Total Orders chart)
const CustomXAxisTick = ({ x, y, payload, maxWidth, fontSize, angle = -45, formatter }) => {
  const text = formatter ? formatter(payload.value) : payload.value;
  const actualAngle = angle;
  const actualTextAnchor = 'end';

  // Adjust font size based on screen width for better readability
  const screenWidth = window.innerWidth;
  const responsiveFontSize = screenWidth < 640 ? Math.max(9, fontSize * 0.85) : fontSize;

  // For angled labels, wrap text if needed - use larger maxWidth to reduce wrapping
  const effectiveMaxWidth = screenWidth < 640 ? maxWidth * 1.3 : maxWidth;
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const testWidth = testLine.length * (responsiveFontSize * 0.6);
    if (testWidth > effectiveMaxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);

  // Increase line height for better readability
  const lineHeight = responsiveFontSize * 1.3;

  return (
    <g>
      {lines.map((line, i) => (
        <text
          key={i}
          x={x}
          y={y + i * lineHeight}
          textAnchor={actualTextAnchor}
          fontSize={responsiveFontSize}
          fontWeight={600}
          fill="#374151"
          transform={`rotate(${actualAngle} ${x} ${y + i * lineHeight})`}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/**
 * TopProductsGroupedChart - Displays top selling products grouped by oil
 * with clustered bars for each bottle size
 *
 * @param {Array} data - Array of objects with structure:
 *   [{ oil: 'Oil Name', '30ml': 10, '50ml': 20, '100ml': 15, '20ml': 5, '5ml': 3 }]
 * @param {Array} availableSizes - Array of available bottle sizes from database (optional)
 * @param {number} height - Chart height in pixels (default: 360)
 */
const TopProductsGroupedChart = ({ data, availableSizes, height = 360 }) => {
  const { t } = useLanguage();
  // Debug: Log the data to see what we're working with
  console.log('TopProductsGroupedChart data:', data);
  console.log('TopProductsGroupedChart availableSizes:', availableSizes);

  // Use provided sizes or fall back to default sizes
  // Extract sizes from data if availableSizes not provided
  const bottleSizes = React.useMemo(() => {
    if (availableSizes && availableSizes.length > 0) {
      return availableSizes;
    }

    // Extract sizes from data
    const sizesFromData = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'oil' && key !== 'total' && item[key] > 0) {
          sizesFromData.add(key);
        }
      });
    });

    // Sort sizes in a logical order
    const sizeOrder = ['5ml', '20ml', '30ml', '50ml', '100ml', 'tester'];
    return Array.from(sizesFromData).sort((a, b) => {
      const indexA = sizeOrder.indexOf(a);
      const indexB = sizeOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [data, availableSizes]);

  // Define colors for each bottle size (vibrant, distinct colors)
  // Generate colors dynamically if we have more sizes than predefined colors
  const predefinedColors = {
    '5ml': '#ec4899',    // Pink-500 (easier on eyes than red)
    '20ml': '#f59e0b',   // Amber-500
    '30ml': '#10b981',   // Emerald-500
    '50ml': '#3b82f6',   // Blue-500
    '100ml': '#8b5cf6',  // Violet-500
    'tester': '#f43f5e', // Rose-500
  };

  const colorPalette = ['#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#f43f5e', '#06b6d4', '#8b5cf6'];

  const sizeColors = React.useMemo(() => {
    const colors = {};
    bottleSizes.forEach((size, index) => {
      colors[size] = predefinedColors[size] || colorPalette[index % colorPalette.length];
    });
    return colors;
  }, [bottleSizes]);

  // Prepare series data for each bottle size
  const series = bottleSizes.map(size => ({
    dataKey: size,
    label: size,
    color: sizeColors[size],
    valueFormatter: (value) => {
      // Return null for 0 or undefined values to hide them from tooltip
      if (!value || value === 0) return null;
      return `${value} orders`;
    },
    stack: undefined, // Ensure bars are clustered, not stacked
  }));

  // Calculate appropriate categoryGapRatio based on number of products
  // When there are few products (1-2), increase the gap to limit maximum bar width
  const categoryGapRatio = React.useMemo(() => {
    if (data.length === 1) return 0.85; // Large gap for single product (max ~15% width)
    if (data.length === 2) return 0.75; // Large gap for two products (max ~25% width)
    if (data.length === 3) return 0.65; // Moderate gap for three products
    return 0.3; // Default gap for 4+ products
  }, [data.length]);

  // Calculate base font size ONCE on mount (same as Total Orders chart chartLabelFontSize)
  const baseFontSize = React.useMemo(() => {
    return Math.max(12, Math.min(14, window.innerWidth * 0.018));
  }, []); // Empty dependency array - calculate once and never change

  const labelMaxWidth = React.useMemo(() => {
    return Math.max(60, window.innerWidth * 0.1);
  }, []); // Empty dependency array - calculate once and never change

  // Calculate legend font size and icon size based on screen width
  const legendFontSize = React.useMemo(() => {
    return window.innerWidth < 640 ? 10 : 12;
  }, []);

  const legendIconSize = React.useMemo(() => {
    return window.innerWidth < 640 ? 8 : 12;
  }, []);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        margin={{ bottom: 80, top: 10, right: 5, left: 0 }}
        barCategoryGap="15%"
        barGap={2}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
        <XAxis
          dataKey="oil"
          tick={<CustomXAxisTick
            fontSize={baseFontSize}
            maxWidth={labelMaxWidth}
            angle={-45}
          />}
          tickMargin={15}
          interval={0}
          axisLine={false}
          tickLine={false}
        />
        <YAxis axisLine={false} tickLine={false} />
        <Tooltip />
        <Legend
          verticalAlign="top"
          align="center"
          height={36}
          iconType="square"
          iconSize={legendIconSize}
          formatter={(value) => t(value.toLowerCase().replace(/ /g, '')) || t(value) || value}
          wrapperStyle={{
            paddingTop: '4px',
            paddingBottom: '30px',
            paddingLeft: '10px',
            paddingRight: '10px',
            fontSize: `${legendFontSize}px`
          }}
        />
        {bottleSizes.map((size) => (
          <Bar
            key={size}
            dataKey={size}
            fill={sizeColors[size]}
            radius={[4, 4, 0, 0]}
            maxBarSize={40}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default TopProductsGroupedChart;

